import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Lock, Eye, EyeOff, Check, AlertCircle } from 'lucide-react';
import { userService } from '../features/auth/services/userService';
import OTPVerification from './OTPVerification';

const ChangePassword = ({ isOpen, onClose, onSuccess }) => {
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [showOTPModal, setShowOTPModal] = useState(false);

    const validatePassword = () => {
        if (!currentPassword) {
            setError('Current password is required');
            return false;
        }

        if (!newPassword) {
            setError('New password is required');
            return false;
        }

        if (!confirmPassword) {
            setError('Confirm password is required');
            return false;
        }

        if (newPassword.length < 8) {
            setError('New password must be at least 8 characters');
            return false;
        }

        if (newPassword !== confirmPassword) {
            setError('New password and confirm password do not match');
            return false;
        }

        if (currentPassword === newPassword) {
            setError('New password must be different from current password');
            return false;
        }

        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        if (!validatePassword()) {
            return;
        }

        setLoading(true);

        try {
            const response = await userService.changePassword(currentPassword, newPassword, confirmPassword);

            console.log('Change Password Response:', response);

            if (response.message) {
                // OTP sent successfully, show OTP modal
                setShowOTPModal(true);
            }
        } catch (err) {
            console.error('Change password error:', err);
            console.error('Error response:', err.response);
            console.error('Error data:', err.response?.data);

            // Extract error message from various possible response formats
            let errorMessage = 'Failed to change password. Please try again.';

            if (err.response?.data) {
                const data = err.response.data;

                // Check for different error message formats
                if (data.message) {
                    errorMessage = data.message;
                } else if (data.error) {
                    errorMessage = data.error;
                } else if (data.detail) {
                    errorMessage = data.detail;
                } else if (typeof data === 'string') {
                    errorMessage = data;
                } else if (data.current_password) {
                    // Field-specific error
                    errorMessage = Array.isArray(data.current_password)
                        ? data.current_password[0]
                        : data.current_password;
                } else if (data.new_password) {
                    errorMessage = Array.isArray(data.new_password)
                        ? data.new_password[0]
                        : data.new_password;
                } else if (data.confirm_password) {
                    errorMessage = Array.isArray(data.confirm_password)
                        ? data.confirm_password[0]
                        : data.confirm_password;
                }
            }

            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setShowCurrentPassword(false);
        setShowNewPassword(false);
        setShowConfirmPassword(false);
        setError(null);
        setSuccess(false);
        setShowOTPModal(false);
        onClose();
    };

    const handleOTPSuccess = () => {
        setSuccess(true);
        setShowOTPModal(false);

        // Call success callback
        if (onSuccess) {
            onSuccess();
        }

        // Close modal after 2 seconds
        setTimeout(() => {
            handleClose();
        }, 2000);
    };

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0, 0, 0, 0.75)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000,
                    padding: '20px'
                }}
                onClick={handleClose}
            >
                <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.9, opacity: 0 }}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                        background: '#111827',
                        borderRadius: '12px',
                        padding: '24px',
                        maxWidth: '500px',
                        width: '100%',
                        border: '1px solid #1f2937',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.5)'
                    }}
                >
                    {/* Header */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '24px'
                    }}>
                        <h2 style={{
                            fontSize: '20px',
                            fontWeight: '600',
                            color: '#fff',
                            margin: 0,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px'
                        }}>
                            <Lock size={24} />
                            Change Password
                        </h2>
                        <button
                            onClick={handleClose}
                            style={{
                                background: 'transparent',
                                border: 'none',
                                color: '#9ca3af',
                                cursor: 'pointer',
                                padding: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: '4px',
                                transition: 'all 0.2s'
                            }}
                            onMouseOver={(e) => {
                                e.currentTarget.style.background = '#1f2937';
                                e.currentTarget.style.color = '#fff';
                            }}
                            onMouseOut={(e) => {
                                e.currentTarget.style.background = 'transparent';
                                e.currentTarget.style.color = '#9ca3af';
                            }}
                        >
                            <X size={20} />
                        </button>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit}>
                        {/* Current Password */}
                        <div style={{ marginBottom: '20px' }}>
                            <label style={{
                                display: 'block',
                                fontSize: '13px',
                                fontWeight: '500',
                                color: '#e5e7eb',
                                marginBottom: '8px'
                            }}>
                                Current Password
                            </label>
                            <div style={{ position: 'relative' }}>
                                <input
                                    type={showCurrentPassword ? 'text' : 'password'}
                                    value={currentPassword}
                                    onChange={(e) => setCurrentPassword(e.target.value)}
                                    disabled={loading || success}
                                    style={{
                                        width: '100%',
                                        padding: '10px 40px 10px 12px',
                                        background: '#0f1419',
                                        border: '1px solid #374151',
                                        borderRadius: '6px',
                                        color: '#fff',
                                        fontSize: '14px',
                                        outline: 'none',
                                        transition: 'border-color 0.2s',
                                        boxSizing: 'border-box'
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                                    onBlur={(e) => e.target.style.borderColor = '#374151'}
                                    placeholder="Enter current password"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                    style={{
                                        position: 'absolute',
                                        right: '10px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        background: 'transparent',
                                        border: 'none',
                                        color: '#9ca3af',
                                        cursor: 'pointer',
                                        padding: '4px',
                                        display: 'flex',
                                        alignItems: 'center'
                                    }}
                                >
                                    {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                </button>
                            </div>
                        </div>

                        {/* New Password */}
                        <div style={{ marginBottom: '20px' }}>
                            <label style={{
                                display: 'block',
                                fontSize: '13px',
                                fontWeight: '500',
                                color: '#e5e7eb',
                                marginBottom: '8px'
                            }}>
                                New Password
                            </label>
                            <div style={{ position: 'relative' }}>
                                <input
                                    type={showNewPassword ? 'text' : 'password'}
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    disabled={loading || success}
                                    style={{
                                        width: '100%',
                                        padding: '10px 40px 10px 12px',
                                        background: '#0f1419',
                                        border: '1px solid #374151',
                                        borderRadius: '6px',
                                        color: '#fff',
                                        fontSize: '14px',
                                        outline: 'none',
                                        transition: 'border-color 0.2s',
                                        boxSizing: 'border-box'
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                                    onBlur={(e) => e.target.style.borderColor = '#374151'}
                                    placeholder="Enter new password (min 8 characters)"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowNewPassword(!showNewPassword)}
                                    style={{
                                        position: 'absolute',
                                        right: '10px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        background: 'transparent',
                                        border: 'none',
                                        color: '#9ca3af',
                                        cursor: 'pointer',
                                        padding: '4px',
                                        display: 'flex',
                                        alignItems: 'center'
                                    }}
                                >
                                    {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                </button>
                            </div>
                        </div>

                        {/* Confirm Password */}
                        <div style={{ marginBottom: '20px' }}>
                            <label style={{
                                display: 'block',
                                fontSize: '13px',
                                fontWeight: '500',
                                color: '#e5e7eb',
                                marginBottom: '8px'
                            }}>
                                Confirm Password
                            </label>
                            <div style={{ position: 'relative' }}>
                                <input
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    disabled={loading || success}
                                    style={{
                                        width: '100%',
                                        padding: '10px 40px 10px 12px',
                                        background: '#0f1419',
                                        border: '1px solid #374151',
                                        borderRadius: '6px',
                                        color: '#fff',
                                        fontSize: '14px',
                                        outline: 'none',
                                        transition: 'border-color 0.2s',
                                        boxSizing: 'border-box'
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                                    onBlur={(e) => e.target.style.borderColor = '#374151'}
                                    placeholder="Re-enter new password"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    style={{
                                        position: 'absolute',
                                        right: '10px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        background: 'transparent',
                                        border: 'none',
                                        color: '#9ca3af',
                                        cursor: 'pointer',
                                        padding: '4px',
                                        display: 'flex',
                                        alignItems: 'center'
                                    }}
                                >
                                    {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                </button>
                            </div>
                        </div>

                        {/* Submit Button */}
                        <button
                            type="submit"
                            disabled={loading || success}
                            style={{
                                width: '100%',
                                padding: '12px 20px',
                                background: success ? '#22c55e' : '#3b82f6',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '8px',
                                fontSize: '14px',
                                fontWeight: '600',
                                cursor: loading || success ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px',
                                transition: 'all 0.2s',
                                opacity: loading ? 0.6 : 1
                            }}
                            onMouseOver={(e) => {
                                if (!loading && !success) {
                                    e.currentTarget.style.background = '#2563eb';
                                }
                            }}
                            onMouseOut={(e) => {
                                if (!loading && !success) {
                                    e.currentTarget.style.background = '#3b82f6';
                                }
                            }}
                        >
                            {loading ? (
                                <>
                                    <div style={{
                                        width: '18px',
                                        height: '18px',
                                        border: '2px solid #fff',
                                        borderTopColor: 'transparent',
                                        borderRadius: '50%',
                                        animation: 'spin 0.8s linear infinite'
                                    }} />
                                    Changing Password...
                                </>
                            ) : success ? (
                                <>
                                    <Check size={18} />
                                    Password Changed Successfully!
                                </>
                            ) : (
                                <>
                                    <Lock size={18} />
                                    Change Password
                                </>
                            )}
                        </button>
                    </form>

                    {/* Error Message */}
                    {error && (
                        <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            style={{
                                marginTop: '16px',
                                padding: '12px',
                                background: 'rgba(239, 68, 68, 0.1)',
                                border: '1px solid #ef4444',
                                borderRadius: '6px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                color: '#ef4444',
                                fontSize: '13px'
                            }}
                        >
                            <AlertCircle size={16} />
                            {error}
                        </motion.div>
                    )}

                    {/* Success Message */}
                    {success && (
                        <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            style={{
                                marginTop: '16px',
                                padding: '12px',
                                background: 'rgba(34, 197, 94, 0.1)',
                                border: '1px solid #22c55e',
                                borderRadius: '6px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                color: '#22c55e',
                                fontSize: '13px'
                            }}
                        >
                            <Check size={16} />
                            Password changed successfully! You can now use your new password to login.
                        </motion.div>
                    )}

                    {/* Password Requirements */}
                    <div style={{
                        marginTop: '16px',
                        padding: '12px',
                        background: '#0f1419',
                        borderRadius: '6px',
                        fontSize: '12px',
                        color: '#9ca3af'
                    }}>
                        <div style={{ fontWeight: '600', color: '#e5e7eb', marginBottom: '6px' }}>
                            Password Requirements:
                        </div>
                        <ul style={{ margin: 0, paddingLeft: '20px' }}>
                            <li>At least 8 characters long</li>
                            <li>New password and confirm password must match</li>
                            <li>Must be different from current password</li>
                        </ul>
                    </div>
                </motion.div>

                {/* OTP Verification Modal */}
                <OTPVerification
                    isOpen={showOTPModal}
                    onClose={() => setShowOTPModal(false)}
                    onSuccess={handleOTPSuccess}
                />

                {/* CSS Animation for spinner */}
                <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
            </motion.div>
        </AnimatePresence>
    );
};

export default ChangePassword;
